import React, { useState, useEffect, useCallback } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  getPropertyBreakdown,
  PropertyBreakdown,
} from '../services/pocketsmith.service';
import { tw } from '../utils/terminalTheme';
import { FiHome, FiTrendingUp, FiTrendingDown, FiDollarSign } from 'react-icons/fi';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const monoFont = "'JetBrains Mono', 'Fira Code', monospace";

// Generate consistent colors for properties
const propertyColors = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#f97316', '#ec4899', '#14b8a6', '#a855f7',
  '#6366f1', '#22c55e', '#eab308', '#dc2626', '#0ea5e9',
  '#d946ef', '#84cc16', '#f43f5e', '#2dd4bf', '#c084fc',
  '#fb923c', '#34d399', '#facc15', '#fb7185', '#38bdf8',
  '#a78bfa', '#4ade80', '#fbbf24', '#f87171', '#67e8f9',
  '#818cf8', '#86efac', '#fde047', '#fca5a5', '#7dd3fc',
];

const getPropertyColor = (index: number): string => propertyColors[index % propertyColors.length];

const fmt = (n: number) => '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2 });

type SortKey = 'net' | 'expenses' | 'income' | 'count' | 'name';

export const PropertySpending: React.FC = () => {
  const [data, setData] = useState<PropertyBreakdown[]>([]);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(12);
  const [accountGroup, setAccountGroup] = useState('');
  const [excludeTransfers, setExcludeTransfers] = useState(true);
  const [sortBy, setSortBy] = useState<SortKey>('net');
  const [showIncome, setShowIncome] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPropertyBreakdown({
        months,
        account_group: accountGroup || undefined,
        exclude_transfers: excludeTransfers,
      });
      setData(res.data || []);
    } catch (err) {
      console.error('Property spending load failed', err);
    } finally {
      setLoading(false);
    }
  }, [months, accountGroup, excludeTransfers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Sort data
  const sortedData = [...data].sort((a, b) => {
    switch (sortBy) {
      case 'net': return a.net - b.net;
      case 'expenses': return a.expenses - b.expenses;
      case 'income': return b.income - a.income;
      case 'count': return b.count - a.count;
      case 'name': return a.property_name.localeCompare(b.property_name);
      default: return a.net - b.net;
    }
  });

  // Totals
  const totalIncome = data.reduce((s, d) => s + d.income, 0);
  const totalExpenses = data.reduce((s, d) => s + d.expenses, 0);
  const totalNet = data.reduce((s, d) => s + d.net, 0);
  const totalTxns = data.reduce((s, d) => s + d.count, 0);

  // --- Net by Property Chart (horizontal bar) ---
  const netChartData = {
    labels: sortedData.map(d => {
      const name = d.property_name;
      return name.length > 30 ? name.substring(0, 28) + '...' : name;
    }),
    datasets: [{
      label: 'Net ($)',
      data: sortedData.map(d => d.net),
      backgroundColor: sortedData.map((d, i) => d.net >= 0 ? 'rgba(34, 197, 94, 0.7)' : 'rgba(239, 68, 68, 0.7)'),
      borderColor: sortedData.map((d, i) => d.net >= 0 ? '#22c55e' : '#ef4444'),
      borderWidth: 1,
      borderRadius: 4,
      barThickness: 20,
    }],
  };

  const netChartOptions: any = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1f2937',
        titleColor: '#22d3ee',
        bodyColor: '#d1d5db',
        borderColor: '#374151',
        borderWidth: 1,
        titleFont: { family: monoFont },
        bodyFont: { family: monoFont },
        callbacks: {
          label: (ctx: any) => {
            const val = ctx.parsed.x;
            return `${val >= 0 ? '+' : '-'}$${Math.abs(val).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#6b7280',
          font: { family: monoFont, size: 11 },
          callback: (val: number) => {
            const abs = Math.abs(val);
            return (val < 0 ? '-' : '') + (abs >= 1000 ? '$' + (abs / 1000).toFixed(0) + 'k' : '$' + abs);
          },
        },
        grid: { color: 'rgba(55, 65, 81, 0.3)' },
      },
      y: {
        ticks: { color: '#d1d5db', font: { family: monoFont, size: 11 } },
        grid: { display: false },
      },
    },
  };

  // --- Income vs Expenses Chart (grouped bar) ---
  const incExpChartData = {
    labels: sortedData.map(d => {
      const name = d.property_name;
      return name.length > 20 ? name.substring(0, 18) + '...' : name;
    }),
    datasets: [
      ...(showIncome ? [{
        label: 'Income',
        data: sortedData.map(d => d.income),
        backgroundColor: 'rgba(34, 197, 94, 0.7)',
        borderColor: '#22c55e',
        borderWidth: 1,
        borderRadius: 4,
      }] : []),
      {
        label: 'Expenses',
        data: sortedData.map(d => Math.abs(d.expenses)),
        backgroundColor: 'rgba(239, 68, 68, 0.7)',
        borderColor: '#ef4444',
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const incExpChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: '#9ca3af',
          font: { family: monoFont, size: 12 },
          usePointStyle: true,
          pointStyle: 'rectRounded',
        },
      },
      tooltip: {
        backgroundColor: '#1f2937',
        titleColor: '#22d3ee',
        bodyColor: '#d1d5db',
        borderColor: '#374151',
        borderWidth: 1,
        titleFont: { family: monoFont },
        bodyFont: { family: monoFont },
        callbacks: {
          label: (ctx: any) => `${ctx.dataset.label}: $${ctx.parsed.y.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
        },
      },
    },
    scales: {
      x: {
        ticks: { color: '#6b7280', font: { family: monoFont, size: 10 }, maxRotation: 45 },
        grid: { color: 'rgba(55, 65, 81, 0.3)' },
      },
      y: {
        ticks: {
          color: '#6b7280',
          font: { family: monoFont, size: 11 },
          callback: (val: number) => val >= 1000 ? '$' + (val / 1000).toFixed(0) + 'k' : '$' + val,
        },
        grid: { color: 'rgba(55, 65, 81, 0.3)' },
      },
    },
  };

  const groupButtons = [
    { key: '', label: 'All', color: 'text-white' },
    { key: 'PERSONAL', label: 'Personal', color: 'text-amber-400' },
    { key: 'BUSINESS', label: 'Business', color: 'text-blue-400' },
    { key: 'INVESTMENT', label: 'Investment', color: 'text-emerald-400' },
  ];

  const sortButtons: { key: SortKey; label: string }[] = [
    { key: 'net', label: 'Net' },
    { key: 'expenses', label: 'Expenses' },
    { key: 'income', label: 'Income' },
    { key: 'count', label: 'Txns' },
    { key: 'name', label: 'Name' },
  ];

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-white" style={{ fontFamily: monoFont }}>
          <span className="text-cyan-400">~/</span>properties/spending
          <span className="text-green-500 text-base ml-2">$</span>
          <span className="text-gray-500 text-base ml-1">breakdown --by-property</span>
        </h1>
        <p className="text-gray-500 text-sm mt-1" style={{ fontFamily: monoFont }}>
          Income, expenses, and net cash flow by property
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex items-center gap-1 bg-gray-800/50 rounded-lg p-1">
          {groupButtons.map(g => (
            <button
              key={g.key}
              onClick={() => setAccountGroup(g.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-mono transition-all ${
                accountGroup === g.key
                  ? 'bg-gray-700 ' + g.color + ' font-semibold'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>

        <button
          onClick={() => setExcludeTransfers(!excludeTransfers)}
          className={`px-3 py-1.5 rounded-md text-xs font-mono transition-all ${
            excludeTransfers
              ? 'bg-cyan-600/20 text-cyan-400 border border-cyan-500/30'
              : 'bg-gray-800/50 text-gray-500 border border-gray-700/30'
          }`}
        >
          {excludeTransfers ? '✓ ' : ''}Hide Transfers
        </button>

        <select
          value={months}
          onChange={e => setMonths(Number(e.target.value))}
          className="bg-gray-800/50 border border-gray-700/30 text-gray-300 text-xs font-mono rounded-md px-3 py-1.5"
        >
          <option value={3}>3 months</option>
          <option value={6}>6 months</option>
          <option value={12}>12 months</option>
          <option value={24}>24 months</option>
        </select>

        <div className="flex items-center gap-1 bg-gray-800/50 rounded-lg p-1 ml-auto">
          <span className="text-gray-600 text-xs font-mono px-2">Sort:</span>
          {sortButtons.map(s => (
            <button
              key={s.key}
              onClick={() => setSortBy(s.key)}
              className={`px-2 py-1 rounded-md text-xs font-mono transition-all ${
                sortBy === s.key
                  ? 'bg-gray-700 text-cyan-400 font-semibold'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className={tw.statCard}>
          <div className={tw.statLabel}>Properties</div>
          <div className="text-cyan-400 text-xl font-bold font-mono">{data.length}</div>
        </div>
        <div className={tw.statCard}>
          <div className={tw.statLabel}>Total Income</div>
          <div className="text-green-400 text-xl font-bold font-mono">+{fmt(totalIncome)}</div>
        </div>
        <div className={tw.statCard}>
          <div className={tw.statLabel}>Total Expenses</div>
          <div className="text-red-400 text-xl font-bold font-mono">-{fmt(totalExpenses)}</div>
        </div>
        <div className={tw.statCard}>
          <div className={tw.statLabel}>Net Cash Flow</div>
          <div className={`text-xl font-bold font-mono ${totalNet >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {totalNet >= 0 ? '+' : '-'}{fmt(totalNet)}
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-center text-gray-500 py-12 font-mono">Loading property data...</div>
      )}

      {!loading && (
        <div className="space-y-6">
          {/* Net Cash Flow by Property */}
          <div className={tw.tableWrap}>
            <div className="flex items-center gap-2 mb-4 px-4 pt-4">
              <FiHome className="text-cyan-400" />
              <h2 className="text-white font-semibold font-mono">Net Cash Flow by Property</h2>
              <span className="text-gray-600 text-xs font-mono ml-auto">{months} months | {sortedData.length} properties</span>
            </div>
            {sortedData.length > 0 ? (
              <div className="px-4 pb-4">
                <div style={{ height: Math.max(400, sortedData.length * 28) + 'px' }}>
                  <Bar data={netChartData} options={netChartOptions} />
                </div>
              </div>
            ) : (
              <div className="text-gray-500 text-center py-8 font-mono text-sm">No property data</div>
            )}
          </div>

          {/* Income vs Expenses by Property */}
          <div className={tw.tableWrap}>
            <div className="flex items-center gap-2 mb-4 px-4 pt-4">
              <FiDollarSign className="text-cyan-400" />
              <h2 className="text-white font-semibold font-mono">Income vs Expenses by Property</h2>
              <button
                onClick={() => setShowIncome(!showIncome)}
                className={`ml-4 px-2 py-0.5 rounded text-xs font-mono transition-all ${
                  showIncome
                    ? 'bg-green-600/20 text-green-400 border border-green-500/30'
                    : 'bg-gray-800/50 text-gray-500 border border-gray-700/30'
                }`}
              >
                {showIncome ? '✓ ' : ''}Income
              </button>
            </div>
            {sortedData.length > 0 ? (
              <div className="px-4 pb-4">
                <div style={{ height: '500px' }}>
                  <Bar data={incExpChartData} options={incExpChartOptions} />
                </div>
              </div>
            ) : (
              <div className="text-gray-500 text-center py-8 font-mono text-sm">No property data</div>
            )}
          </div>

          {/* Property Table */}
          <div className={tw.tableWrap}>
            <div className="flex items-center gap-2 mb-4 px-4 pt-4">
              <FiTrendingUp className="text-cyan-400" />
              <h2 className="text-white font-semibold font-mono">Property Detail</h2>
              <span className="text-gray-600 text-xs font-mono ml-auto">{totalTxns} total transactions</span>
            </div>
            <div className="px-4 pb-4">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-2 text-xs font-mono text-gray-500 border-b border-gray-700/30 pb-2 mb-2">
                <div className="col-span-4">Property</div>
                <div className="col-span-2 text-right">Income</div>
                <div className="col-span-2 text-right">Expenses</div>
                <div className="col-span-2 text-right">Net</div>
                <div className="col-span-1 text-right">Txns</div>
                <div className="col-span-1 text-right">Margin</div>
              </div>
              {/* Rows */}
              {sortedData.map((d, i) => {
                const margin = d.income > 0 ? ((d.net / d.income) * 100).toFixed(0) : d.expenses < 0 ? '-100' : '0';
                return (
                  <div
                    key={d.property_id}
                    className="grid grid-cols-12 gap-2 text-xs font-mono py-1.5 border-b border-gray-800/30 hover:bg-gray-800/20 transition-colors"
                  >
                    <div className="col-span-4 flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-sm inline-block flex-shrink-0"
                        style={{ backgroundColor: getPropertyColor(i) }}
                      />
                      <a
                        href={`/properties/${d.property_id}`}
                        className="text-gray-300 hover:text-cyan-400 transition-colors truncate"
                      >
                        {d.property_name}
                      </a>
                    </div>
                    <div className="col-span-2 text-right text-green-400">
                      {d.income > 0 ? '+' + fmt(d.income) : '-'}
                    </div>
                    <div className="col-span-2 text-right text-red-400">
                      {d.expenses < 0 ? '-' + fmt(d.expenses) : '-'}
                    </div>
                    <div className={`col-span-2 text-right font-semibold ${d.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {d.net >= 0 ? '+' : '-'}{fmt(d.net)}
                    </div>
                    <div className="col-span-1 text-right text-gray-500">
                      {d.count}
                    </div>
                    <div className={`col-span-1 text-right ${Number(margin) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {margin}%
                    </div>
                  </div>
                );
              })}
              {/* Totals Row */}
              <div className="grid grid-cols-12 gap-2 text-xs font-mono py-2 mt-1 border-t border-gray-600/30 font-semibold">
                <div className="col-span-4 text-gray-300">TOTAL ({data.length} properties)</div>
                <div className="col-span-2 text-right text-green-400">+{fmt(totalIncome)}</div>
                <div className="col-span-2 text-right text-red-400">-{fmt(totalExpenses)}</div>
                <div className={`col-span-2 text-right ${totalNet >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {totalNet >= 0 ? '+' : '-'}{fmt(totalNet)}
                </div>
                <div className="col-span-1 text-right text-gray-400">{totalTxns}</div>
                <div className={`col-span-1 text-right ${totalIncome > 0 ? (totalNet / totalIncome * 100 >= 0 ? 'text-green-400' : 'text-red-400') : 'text-gray-500'}`}>
                  {totalIncome > 0 ? ((totalNet / totalIncome) * 100).toFixed(0) + '%' : '-'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
